import { FC } from 'react';
import { z } from 'zod';
import { IntlShape, useIntl } from 'react-intl';
import { useTypedForm } from '../../../../../../../shared/fe/hooks/useForm';
import { useApiMutation } from '../../../../../query/hooks';
import { useModalContext } from '../../../../../modal/ModalContext';
import { graphql } from '../../../../../gql';
import { ModalContent } from '../../../../../modal/components/ModalContent';
import { FormField } from '../../../../../form/FormField';
import { ModalActions } from '../../../../../modal/components/ModalActions';
import { OutlinedInput } from '@mui/material';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import * as qs from 'qs';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from '../../../../../store/snackbar';

const createOpportunityAndAddToActivityModal_createOpportunity = graphql(`
	mutation createOpportunityAndAddToActivityModal_createOpportunity(
		$name: String!
		$description: String
		$benefit: Int
		$status: OpportunityStatus!
		$links: Json!
	) {
		createOpportunity(data: { name: $name, description: $description, benefit: $benefit, status: $status, links: $links }) {
			ok
			errorMessage
			node {
				id
			}
		}
	}
`);

const createOpportunityAndAddToActivityModal_updateActivity = graphql(`
	mutation createOpportunityAndAddToActivityModal_updateActivity($activityId: UUID!, $opportunityId: UUID!) {
		updateActivity(by: { id: $activityId }, data: { opportunities: { connect: { id: $opportunityId } } }) {
			ok
			errorMessage
		}
	}
`);

const createOpportunityAndAddToActivityModalSchema = z.object({
	activityId: z.string(),
});

type Props = z.infer<typeof createOpportunityAndAddToActivityModalSchema>;

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().min(1, intl.formatMessage({ id: 'opportunity.detail.name.error.empty-name' })),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const CreateOpportunityAndAddToActivityModal: FC<Props> = ({ activityId }) => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const { openSnackbar } = useSnackbar();
	const { mutateAsync: createOpportunity, isPending: isCreateOpportunityPending } = useApiMutation(
		createOpportunityAndAddToActivityModal_createOpportunity,
	);
	const { mutateAsync: updateActivity, isPending: isUpdateActivityPending } = useApiMutation(
		createOpportunityAndAddToActivityModal_updateActivity,
	);

	const form = useTypedForm({
		schema: createFormSchema(intl),
		defaultValues: {
			name: '',
		},
	});

	const submitDisabled = !form.formState.isValid || isCreateOpportunityPending || isUpdateActivityPending;

	const onSubmit = async ({ name }: FormValues) => {
		let result = true;
		let error = '';
		let errorMessage: string | null | undefined = '';

		const createResult = await createOpportunity({
			name,
			description: '',
			benefit: 5,
			status: 'new',
			links: { values: [] },
		});

		if (!createResult.createOpportunity.ok) {
			result = false;
			error = intl.formatMessage({ id: 'opportunity.list.create.error' });
			errorMessage = createResult.createOpportunity.errorMessage;
		}

		const opportunityId = createResult.createOpportunity.node?.id;

		if (opportunityId) {
			const updateResult = await updateActivity({ activityId, opportunityId });

			if (!updateResult.updateActivity.ok) {
				result = false;
				error = intl.formatMessage({ id: 'journey.detail.widget.opportunities.add.error' });
				errorMessage = updateResult.updateActivity.errorMessage;
			}
		}

		mutationResponseHandler({
			result,
			toastMessages: {
				success: intl.formatMessage({ id: 'opportunity.list.create.success' }),
				error,
			},
			errorMessage,
			openSnackbar,
			callback: () => {
				/**
				 * NOTE: `react-router-dom`'s `setSearchParams` function claims to be an updater function but isn't.
				 * If we dispatch two searchParam changes at once (closeModal and setDrawer),
				 * the output of the first call isn't fed into the second.
				 * We solve this by writing a `closeModal` + `setDrawer` combination here manually.
				 */

				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { modal, ...rest } = qs.parse(searchParams.toString());
				setSearchParams(qs.stringify({ ...rest, drawer: { type: 'editOpportunity', opportunityId } }));
			},
		});
	};

	return (
		<>
			<ModalContent title={intl.formatMessage({ id: 'journey.detail.widget.opportunities.create-modal.title' })} description={''}>
				<form noValidate>
					<FormField
						form={form}
						name={'name'}
						label={intl.formatMessage({ id: 'journey.detail.widget.opportunities.create-modal.name-label' })}
						render={(props) => <OutlinedInput {...props} />}
					/>
				</form>
			</ModalContent>
			<ModalActions
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={form.handleSubmit(onSubmit)}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'journey.detail.widget.opportunities.create-modal.submit' })}
				disablePrimaryButton={submitDisabled}
			/>
		</>
	);
};

export { CreateOpportunityAndAddToActivityModal, createOpportunityAndAddToActivityModalSchema };
